import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const SubscriptionManagePage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const navigate = useNavigate();

  // Datos de prueba que coinciden con los planes
  const mockSubscription = {
    subscriptionId: "sub_abc123xyz789",
    planId: "plan-premium",
    planName: "Plan Premium",
    customerId: "stupidcomedy@gmail.com",
    status: 1, // Activa
    created: "2024-03-15T10:30:00Z", // Hace unos meses para que tenga sentido
    nextInvoiceDate: "2025-07-15T10:30:00Z", // Se actualizará según el plan
    trialEnd: null,
    amount: 4990,
    currency: "CLP",
    interval: "mensual"
  };

  // Planes disponibles (mismos que en SubscriptionPlansPage)
  const availablePlans = [
    {
      id: 'plan-basico',
      name: 'Plan Básico',
      price: 0,
      currency: 'CLP',
      interval: 'mensual',
      features: [
        'Acceso a contenido básico',
        'Anuncios incluidos',
      ],
      popular: false
    },
    {
      id: 'plan-premium',
      name: 'Plan Premium',
      price: 4990,
      currency: 'CLP',
      interval: 'mensual',
      features: [
        'Acceso a todo el contenido',
        'Sin anuncios',
        'Contenido exclusivo',
        'Soporte prioritario',
      ],
      popular: true
    },
    {
      id: 'plan-anual',
      name: 'Plan Premium Anual',
      price: 49990,
      currency: 'CLP',
      interval: 'anual',
      features: [
        'Acceso a todo el contenido',
        'Sin anuncios',
        'Contenido exclusivo',
        'Soporte prioritario',
        '2 meses gratis',            
      ],
      popular: false
    }
  ];

  // Función para calcular fechas lógicas basadas en el plan
  const calculateNextInvoiceDate = (createdDate, interval) => {
    const created = new Date(createdDate);
    const now = new Date();
    
    if (interval === 'mensual') {
      // Para plan mensual: próximo mes desde la fecha de creación
      const nextDate = new Date(created);
      while (nextDate <= now) {
        nextDate.setMonth(nextDate.getMonth() + 1);
      }
      return nextDate.toISOString();
    } else if (interval === 'anual') {
      // Para plan anual: próximo año desde la fecha de creación
      const nextDate = new Date(created);
      while (nextDate <= now) {
        nextDate.setFullYear(nextDate.getFullYear() + 1);
      }
      return nextDate.toISOString();
    }
    
    return null;
  };

  // Función para calcular tiempo suscrito
  const getSubscriptionDuration = (createdDate) => {
    const created = new Date(createdDate);
    const now = new Date();
    const diffTime = Math.abs(now - created);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 30) {
      return `${diffDays} días`;
    } else if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      const remainingDays = diffDays % 30;
      if (remainingDays === 0) {
        return `${months} ${months === 1 ? 'mes' : 'meses'}`;
      }
      return `${months} ${months === 1 ? 'mes' : 'meses'} y ${remainingDays} días`;
    } else {
      const years = Math.floor(diffDays / 365);
      const remainingDays = diffDays % 365;
      const months = Math.floor(remainingDays / 30);
      
      let duration = `${years} ${years === 1 ? 'año' : 'años'}`;
      if (months > 0) {
        duration += ` y ${months} ${months === 1 ? 'mes' : 'meses'}`;
      }
      return duration;
    }
  };

  // Simular carga de datos
  useEffect(() => {
    const loadSubscription = async () => {
      setLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const hasSubscription = true;
      const shouldError = false;
      
      if (shouldError) {
        setError('Error al cargar la información de suscripción');
        setLoading(false);
        return;
      }
      
      if (hasSubscription) {
        // Calcular fecha de próxima facturación basada en el plan
        const updatedSubscription = {
          ...mockSubscription,
          nextInvoiceDate: calculateNextInvoiceDate(mockSubscription.created, mockSubscription.interval)
        };
        setSubscription(updatedSubscription);
      }
      
      setLoading(false);
    };

    loadSubscription();
  }, []);

  const handleCancelSubscription = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    try {
      const shouldFail = false;
      
      if (shouldFail) {
        throw new Error('Error de prueba en cancelación');
      }
      
      setSubscription(prev => ({ ...prev, status: 4 }));
      setShowCancelModal(false);
      
    } catch (err) {
      console.error('Error al cancelar:', err);
      setError('Error al cancelar la suscripción');
    } finally {
      setLoading(false);
    }
  };

  const loadSubscription = async () => {
    setLoading(true);
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Recalcular la fecha de próxima facturación
    setSubscription(prev => ({
      ...prev,
      nextInvoiceDate: calculateNextInvoiceDate(prev.created, prev.interval)
    }));
    
    setLoading(false);
  };

  const formatPrice = (price) => {
    if (price === 0) return 'Gratis';
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusText = (status) => {
    const statusMap = {
      0: 'Inactiva',
      1: 'Activa',
      2: 'En período de prueba',
      4: 'Cancelada'
    };
    return statusMap[status] || 'Desconocido';
  };

  const getStatusColor = (status) => {
    const colorMap = {
      0: 'text-gray-400',
      1: 'text-green-400',
      2: 'text-blue-400',
      4: 'text-red-400'
    };
    return colorMap[status] || 'text-gray-400';
  };

  const getCurrentPlan = () => {
    return availablePlans.find(plan => plan.id === subscription?.planId);
  };

  const getOtherPlans = () => {
    return availablePlans.filter(plan => plan.id !== subscription?.planId);
  };

  // Función para obtener días hasta próxima facturación
  const getDaysUntilNextInvoice = () => {
    if (!subscription?.nextInvoiceDate) return null;
    
    const nextDate = new Date(subscription.nextInvoiceDate);
    const now = new Date();
    const diffTime = nextDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'Vencida';
    if (diffDays === 1) return 'Mañana';
    if (diffDays <= 7) return `En ${diffDays} días`;
    if (diffDays <= 30) return `En ${diffDays} días`;
    
    return `En ${Math.ceil(diffDays / 30)} ${Math.ceil(diffDays / 30) === 1 ? 'mes' : 'meses'}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce delay-1000"></div>
          <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-br from-green-400 to-blue-500 rounded-full animate-pulse delay-2000"></div>
        </div>
        
        <div className="text-center relative z-10">
          <div className="animate-spin rounded-full h-32 w-32 border-b-4 border-gradient-to-r from-purple-500 to-pink-500 mx-auto mb-4"></div>
          <p className="text-xl bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
            Cargando información de suscripción...
          </p>
        </div>
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="min-h-screen bg-black text-white py-20 relative overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
          <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce delay-1000"></div>
        </div>

        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 bg-gradient-to-r from-purple-400 via-pink-500 to-yellow-400 bg-clip-text text-transparent animate-pulse">
            Mi Suscripción
          </h1>
          <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-auto border border-gray-700 shadow-xl">
            <div className="mb-6">
              <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"></path>
              </svg>
            </div>
            <p className="text-gray-300 mb-6 text-lg">No tienes una suscripción activa</p>
            <p className="text-gray-400 mb-8">Únete a miles de usuarios que ya disfrutan del contenido premium</p>
            <button
              onClick={() => navigate('/planes')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-lg font-bold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-purple-500/25"
            >
              Ver Planes Disponibles
            </button>
          </div>
        </div>
      </div>
    );
  }

  const currentPlan = getCurrentPlan();
  const otherPlans = getOtherPlans();
  const subscriptionDuration = getSubscriptionDuration(subscription.created);
  const daysUntilNext = getDaysUntilNextInvoice();

  return (
    <div className="min-h-screen bg-black text-white py-20 relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full animate-pulse"></div>
        <div className="absolute top-40 right-20 w-24 h-24 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full animate-bounce delay-1000"></div>
        <div className="absolute bottom-40 left-1/4 w-40 h-40 bg-gradient-to-br from-green-400 to-blue-500 rounded-full animate-pulse delay-2000"></div>
        <div className="absolute bottom-20 right-1/3 w-28 h-28 bg-gradient-to-br from-red-400 to-purple-500 rounded-full animate-bounce delay-500"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <h1 className="text-4xl md:text-6xl font-bold text-center mb-12 bg-gradient-to-r from-purple-400 via-pink-500 to-yellow-400 bg-clip-text text-transparent animate-pulse">
          Mi Suscripción
        </h1>

        {error && (
          <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg mb-8 max-w-md mx-auto shadow-lg animate-shake">
            {error}
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          {/* Plan Actual */}
          <div className="bg-gray-900 rounded-xl p-8 mb-8 border-2 border-purple-500 shadow-xl shadow-purple-500/25">
            <div className="flex justify-between items-start mb-6">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                    {subscription.planName}
                  </h2>
                  <span className="bg-gradient-to-r from-green-400 to-green-500 text-black px-3 py-1 rounded-full text-sm font-bold">
                    ACTIVO
                  </span>
                </div>
                <p className={`text-lg font-semibold ${getStatusColor(subscription.status)}`}>
                  {getStatusText(subscription.status)}
                </p>
                <p className="text-gray-400 text-sm mt-1">
                  Suscrito desde hace <span className="text-yellow-400 font-semibold">{subscriptionDuration}</span>
                </p>
              </div>
              <div className="text-right">
                <p className="text-2xl font-bold bg-gradient-to-r from-yellow-400 to-orange-500 bg-clip-text text-transparent">
                  {formatPrice(subscription.amount)}
                </p>
                <p className="text-gray-400">/{subscription.interval}</p>
                {subscription.interval === 'anual' && (
                  <p className="text-green-400 text-xs mt-1">
                    ≈ {formatPrice(Math.round(subscription.amount / 12))}/mes
                  </p>
                )}
              </div>
            </div>

            {currentPlan && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-purple-400">Beneficios incluidos:</h3>
                <div className="grid md:grid-cols-2 gap-2">
                  {currentPlan.features.map((feature, index) => (
                    <div key={index} className="flex items-center">
                      <svg className="w-5 h-5 text-green-400 mr-3 animate-pulse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                      </svg>
                      <span className="text-sm">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-6 mb-6 pt-6 border-t border-gray-700">
              <div>
                <h3 className="text-lg font-semibold mb-3 text-yellow-400">Información de la cuenta</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-400">ID de suscripción:</span>
                    <span className="font-mono text-xs">{subscription.subscriptionId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Cliente ID:</span>
                    <span className="font-mono text-xs">{subscription.customerId}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Fecha de inicio:</span>
                    <span>{new Date(subscription.created).toLocaleDateString('es-CL')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-400">Tiempo suscrito:</span>
                    <span className="text-yellow-400 font-semibold">{subscriptionDuration}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold mb-3 text-green-400">
                  Próxima facturación
                </h3>
                <div className="space-y-2 text-sm">
                  {subscription.nextInvoiceDate && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Fecha:</span>
                        <span>{new Date(subscription.nextInvoiceDate).toLocaleDateString('es-CL')}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-400">Tiempo restante:</span>
                        <span className="text-blue-400 font-semibold">{daysUntilNext}</span>
                      </div>
                    </>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-400">Monto:</span>
                    <span className="font-bold text-yellow-400">{formatPrice(subscription.amount)}</span>
                  </div>
                  {subscription.interval === 'anual' && (
                    <div className="text-green-400 text-xs mt-2 p-2 bg-green-400/10 rounded">
                      🎉 ¡Ahorras {formatPrice(4990 * 2)} al año con el plan anual!
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="border-t border-gray-700 pt-6">
              <h3 className="text-lg font-semibold mb-4 text-pink-400">Gestionar suscripción</h3>
              <div className="flex gap-4 flex-wrap">
                <button
                  onClick={() => navigate('/planes')}
                  className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-6 py-2 rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                >
                  Cambiar Plan
                </button>
                
                {subscription.status === 1 && (
                  <button
                    onClick={() => setShowCancelModal(true)}
                    className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    Cancelar Suscripción
                  </button>
                )}
                
                <button
                  onClick={loadSubscription}
                  disabled={loading}
                  className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-2 rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:transform-none"
                >
                  {loading ? 'Actualizando...' : 'Actualizar Estado'}
                </button>
              </div>
            </div>
          </div>

          {/* Otros planes disponibles */}
          {otherPlans.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-6 text-center bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                Otros planes disponibles
              </h2>
              <div className="grid md:grid-cols-2 gap-6">
                {otherPlans.map((plan, index) => (
                  <div
                    key={plan.id}
                    className="bg-gray-900 rounded-xl p-6 border border-gray-700 hover:border-purple-400 transition-all duration-300 transform hover:scale-105 shadow-lg"
                  >
                    <div className="text-center mb-4">
                      <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                      <div className="mb-2">
                        <span className="text-2xl font-bold text-yellow-400">
                          {formatPrice(plan.price)}
                        </span>
                        {plan.price !== 0 && (
                          <span className="text-gray-400">/{plan.interval}</span>
                        )}
                      </div>
                      {plan.interval === 'anual' && (
                        <p className="text-green-400 text-sm">¡Ahorra 2 meses!</p>
                      )}
                    </div>

                    <ul className="space-y-2 mb-4 text-sm">
                      {plan.features.slice(0, 3).map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-center">
                          <svg className="w-4 h-4 text-green-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                          </svg>
                          {feature}
                        </li>
                      ))}
                      {plan.features.length > 3 && (
                        <li className="text-gray-400 text-xs">
                          +{plan.features.length - 3} beneficios más
                        </li>
                      )}
                    </ul>

                    {plan.id !== 'plan-basico' ? (
                      <button
                        onClick={() => navigate('/planes')}
                        className="w-full py-2 px-4 rounded-lg font-bold transition-all duration-300 bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 transform hover:scale-105"
                      >
                        Cambiar a este plan
                      </button>
                    ) : (
                      <div className="text-center text-gray-400 text-sm">
                        Plan gratuito
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Panel de desarrollo */}
          <div className="bg-gray-800 rounded-lg p-4 border border-gray-600">
            <h4 className="text-sm font-semibold mb-2 text-yellow-400">🚧 Panel de Desarrollo</h4>
            <div className="flex gap-2 flex-wrap text-xs">
              <button
                onClick={() => {
                  const newSub = { 
                    ...subscription, 
                    status: 1, 
                    planId: 'plan-premium', 
                    planName: 'Plan Premium',
                    amount: 4990, 
                    interval: 'mensual',
                    nextInvoiceDate: calculateNextInvoiceDate(subscription.created, 'mensual')
                  };
                  setSubscription(newSub);
                }}
                className="bg-purple-600 px-2 py-1 rounded"
              >
                Plan Premium
              </button>
              <button
                onClick={() => {
                  const newSub = {
                    ...subscription, 
                    status: 1, 
                    planId: 'plan-anual', 
                    planName: 'Plan Premium Anual',
                    amount: 49990, 
                    interval: 'anual',
                    nextInvoiceDate: calculateNextInvoiceDate(subscription.created, 'anual')
                  };
                  setSubscription(newSub);
                }}
                className="bg-yellow-600 px-2 py-1 rounded"
              >
                Plan Anual
              </button>
              <button
                onClick={() => setSubscription(prev => ({ ...prev, status: 4 }))}
                className="bg-red-600 px-2 py-1 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={() => setSubscription(null)}
                className="bg-gray-600 px-2 py-1 rounded"
              >
                Sin Suscripción
              </button>
            </div>
          </div>
        </div>

        {/* Modal de cancelación */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
            <div className="bg-gray-900 rounded-xl p-8 max-w-md mx-4 border border-red-500 shadow-xl shadow-red-500/25">
              <h3 className="text-2xl font-bold mb-4 bg-gradient-to-r from-red-400 to-red-500 bg-clip-text text-transparent">
                Cancelar Suscripción
              </h3>
              <div className="mb-6">
                <p className="text-gray-300 mb-4">
                  ¿Estás seguro de que quieres cancelar tu suscripción? 
                </p>
                <div className="bg-gray-800 p-4 rounded-lg mb-4">
                  <p className="text-sm text-gray-400 mb-2">Perderás acceso a:</p>
                  <ul className="text-sm space-y-1">
                    {currentPlan?.features.map((feature, index) => (
                      <li key={index} className="flex items-center text-red-400">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                        </svg>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <p className="text-yellow-400 text-sm">
                  Tu suscripción seguirá activa hasta {subscription?.nextInvoiceDate ? new Date(subscription.nextInvoiceDate).toLocaleDateString('es-CL') : 'el fin del período actual'}.
                </p>
              </div>
              <div className="flex gap-4">
                <button
                  onClick={handleCancelSubscription}
                  disabled={loading}
                  className="bg-gradient-to-r from-red-500 to-red-600 text-white px-6 py-2 rounded-lg hover:from-red-600 hover:to-red-700 disabled:opacity-50 transition-all duration-300 transform hover:scale-105"
                >
                  {loading ? 'Cancelando...' : 'Sí, Cancelar'}
                </button>
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="bg-gradient-to-r from-gray-600 to-gray-700 text-white px-6 py-2 rounded-lg hover:from-gray-700 hover:to-gray-800 transition-all duration-300 transform hover:scale-105"
                >
                  No, Mantener
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-5px); }
          75% { transform: translateX(5px); }
        }
        
        .animate-shake {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default SubscriptionManagePage;